/**
 * messagingClient.js
 * ──────────────────
 * Resolves user-facing copy from a hybrid source:
 *
 *   1. **Defaults** — `config/messaging.json` (ships with code, version-
 *       controlled, edited via PR by a copywriter or via the brand brief).
 *   2. **Overrides** — Mongo `messaging_overrides` collection, fetched
 *       once from `GET /api/mc/messaging/resolved` and cached. Lets a
 *       super-admin tweak any key live, without a deploy.
 *
 * Resolution order:  override → default → fallback string (caller-supplied).
 *
 * The hook `useMessaging(path, fallback)` (see `hooks/useMessaging.js`)
 * is the consumer-facing API. This module owns the cache.
 *
 * Design notes
 * ────────────
 *  • Synchronous reads after the first GET — components don't need to
 *    suspend. We seed from `messaging.json` immediately so the very first
 *    paint is correct even before the override fetch lands.
 *  • Override fetch is fire-and-forget on module load. Once it resolves,
 *    subscribers are notified so any mounted components re-render with
 *    the new values.
 *  • localStorage stash makes repeated page loads instant. Keyed by
 *    version to bust automatically when defaults ship a new schema.
 */
import defaults from '../config/messaging.json';
import api from './api';
import secureStorage from './secureStorage';

const LS_KEY = 'mc_messaging_overrides_v1';
const VERSION = defaults?._meta?.version || 'unversioned';

let _overrides = {};
let _loaded = false;
const _subscribers = new Set();

function _notify() {
  _subscribers.forEach((cb) => {
    try { cb(); } catch { /* swallow — subscriber crash shouldn't break others */ }
  });
}

/** Walks a dot-path into a nested object. Returns `undefined` if missing. */
function _walk(obj, path) {
  if (!path) return undefined;
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

/** Synchronous resolver: override > default > fallback.
 *  Overrides are stored as FLAT keys (e.g. `"hero.headline_main"`) — that's
 *  the shape the backend `PUT /override` endpoint persists. Defaults, in
 *  contrast, are a NESTED object (`{ hero: { headline_main: '…' } }`) for
 *  human readability. So we look up by both shapes here. */
export function getMessage(path, fallback = '') {
  // 1. Flat-key override (the most common case).
  if (Object.prototype.hasOwnProperty.call(_overrides, path)) {
    const v = _overrides[path];
    if (v != null && v !== '') return v;
  }
  // 2. Fallback: if a caller ever feeds us a nested override map (e.g.
  //    from the preview URL where I might switch shapes later), walk it.
  const fromOverrideNested = _walk(_overrides, path);
  if (fromOverrideNested != null && fromOverrideNested !== '') return fromOverrideNested;
  // 3. Default — nested.
  const fromDefault = _walk(defaults, path);
  if (fromDefault != null && fromDefault !== '') return fromDefault;
  return fallback;
}

/** Subscribe to override-cache changes. Returns an unsubscribe. */
export function subscribeMessaging(cb) {
  _subscribers.add(cb);
  return () => _subscribers.delete(cb);
}

/** True once the override fetch has completed (success or fail). */
export function isMessagingLoaded() {
  return _loaded;
}

/** Hydrate from localStorage immediately, then fire the network refresh. */
function _hydrateFromCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed?.version === VERSION && parsed?.overrides) {
      _overrides = parsed.overrides;
    }
  } catch { /* corrupted cache — ignore, the network will repair it */ }
}

async function _refreshFromNetwork() {
  try {
    // Public, chrome-free pages (e.g. /share/report-card/<token>) have no
    // session. Skip the network refresh entirely — defaults/cache are fine
    // and an unauthenticated 401 must never disturb a public viewer.
    const hasSession = !!secureStorage.getItem('session_token');
    if (!hasSession) return;
    const res = await api.get('/mc/messaging/resolved');
    const next = res?.data?.overrides || {};
    // Preview mode wins. If the iframe was bootstrapped with a draft via
    // ?mc_preview_overrides=…, we must NOT clobber it when the network
    // fetch resolves a moment later — that would race-revert the user's
    // draft to the saved state.
    if (!_isPreviewMode) {
      _overrides = next;
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({ version: VERSION, overrides: next }));
      } catch { /* localStorage full or disabled — non-fatal */ }
    }
  } catch (err) {
    // 401 / 404 / network: stay on cached or default values. The product
    // still works perfectly — overrides are an enhancement, not a requirement.
  } finally {
    _loaded = true;
    _notify();
  }
}

// Boot once at module load.
// NOTE: these calls live AT THE BOTTOM of the file so every variable +
// function they touch is fully initialised — `let _isPreviewMode` below
// would otherwise be in the temporal dead-zone and throw a silent
// ReferenceError caught by the try-catches.
_hydrateFromCache();
_refreshFromNetwork();
// preview boot moved below — see end of file

// ── Live-preview support ───────────────────────────────────────
// The admin messaging editor renders this app inside an iframe with
// draft overrides applied so a super-admin can SEE the change before
// saving. Two channels are supported:
//
//   1. URL param  `?mc_preview_overrides=<base64-encoded JSON>`
//      Parsed on boot — used for the iframe's first paint so there
//      is no "default → override" flicker.
//
//   2. postMessage  { type: 'mc_messaging_preview', overrides }
//      Sent from the parent admin page on every (debounced)
//      keystroke. The iframe applies the next set of overrides
//      live without reloading.
//
// Preview mode is intentionally NOT persisted to localStorage so a
// closed/refreshed preview window falls back to the real saved
// state.

let _isPreviewMode = false;

export function isPreviewMode() {
  return _isPreviewMode;
}

function _hydrateFromPreviewUrl() {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const enc = params.get('mc_preview_overrides');
    if (!enc) return;
    // Mirror the unicode-safe encode the admin editor uses:
    //   btoa(unescape(encodeURIComponent(JSON.stringify(x))))
    // Without the symmetric decode, em-dashes / smart quotes corrupt
    // the JSON and the override never applies. Found by smoke test.
    // eslint-disable-next-line no-restricted-globals
    const json = decodeURIComponent(escape(atob(enc)));
    const next = JSON.parse(json);
    if (next && typeof next === 'object') {
      _overrides = next;
      _isPreviewMode = true;
      _loaded = true; // skip the loading "flash"
      _notify();
    }
  } catch {
    // Bad payload — fall back to normal cache/network resolution.
  }
}

function _installPreviewMessageListener() {
  if (typeof window === 'undefined') return;
  window.addEventListener('message', (ev) => {
    // Same-origin only — the parent admin page lives on the same
    // host as the iframe; ignore cross-origin chatter entirely.
    if (ev.origin && ev.origin !== window.location.origin) return;
    const data = ev.data;
    if (!data || data.type !== 'mc_messaging_preview') return;
    if (!data.overrides || typeof data.overrides !== 'object') return;
    _overrides = data.overrides;
    _isPreviewMode = true;
    _loaded = true;
    _notify();
    // Do NOT persist preview state to localStorage — leaving the
    // preview window should restore the real saved values.
  });
}

// Exposed for the admin editor — when a super-admin saves a change, push
// it into the in-memory cache so the UI updates without a reload.
export function applyOverridesLocally(next) {
  _overrides = next || {};
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ version: VERSION, overrides: _overrides }));
  } catch { /* non-fatal */ }
  _notify();
}

// For debugging / tests.
export function _debugSnapshot() {
  return { version: VERSION, loaded: _loaded, overrides: _overrides, defaults };
}

// ── Late boot: preview hooks ───────────────────────────────────
// Now that every `let` above is initialised, it's safe to invoke the
// preview helpers. They look at `_isPreviewMode` and call `_notify()`,
// both of which depend on declarations earlier in this file.
_hydrateFromPreviewUrl();
_installPreviewMessageListener();
// Side-effect flag so the smoke-test can verify the listener really
// is wired up. Read it from the JS console: `window.__mc_msg_ready`.
if (typeof window !== 'undefined') {
  window.__mc_msg_ready = true;
}
